import React, { useState, useEffect } from 'react';
import { getInsights } from 'service/ncoreBoardService';
import styles from './InsightsTab.module.scss';

const LANG_COLORS = {
    JavaScript: '#f1e05a', TypeScript: '#3178c6', Python: '#3572A5',
    CSS: '#563d7c', HTML: '#e34c26', SCSS: '#c6538c',
    Go: '#00ADD8', Rust: '#dea584', Java: '#b07219',
    'C++': '#f34b7d', PHP: '#4F5D95', Ruby: '#701516',
};

export default function InsightsTab({ repo }) {
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getInsights(repo.id).then(data => {
            setInsights(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [repo.id]);

    if (loading) return <div className={styles.loading}>Загрузка статистики...</div>;
    if (!insights) return <div className={styles.empty}>Данные недоступны</div>;

    const { activity = [], commits_by_author = [], language_stats = {}, issue_stats = {}, pr_stats = {}, total_commits = 0, contributors_count = 0 } = insights;

    // Normalize activity: last 30 days
    const maxActivity = Math.max(...activity.map(a => a.count), 1);

    // Language totals
    const langTotal = Object.values(language_stats).reduce((a, b) => a + b, 0);
    const topLangs = Object.entries(language_stats).sort((a, b) => b[1] - a[1]).slice(0, 8);

    return (
        <div className={styles.wrap}>
            {/* Stats overview */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{total_commits}</span>
                    <span className={styles.statLabel}>Всего коммитов</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{contributors_count}</span>
                    <span className={styles.statLabel}>Участников</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{issue_stats.open || 0}</span>
                    <span className={styles.statLabel}>Открытых задач</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{issue_stats.closed || 0}</span>
                    <span className={styles.statLabel}>Закрытых задач</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{pr_stats.open || 0}</span>
                    <span className={styles.statLabel}>Открытых PR</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{pr_stats.merged || 0}</span>
                    <span className={styles.statLabel}>Смерженных PR</span>
                </div>
            </div>

            <div className={styles.chartsRow}>
                {/* Commit Activity (30 days) */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Активность коммитов (30 дней)</h3>
                    {activity.length === 0 ? (
                        <div className={styles.noData}>Нет данных</div>
                    ) : (
                        <div className={styles.barChart}>
                            {activity.map((a, i) => (
                                <div key={i} className={styles.barCol} title={`${a.date}: ${a.count} коммит(ов)`}>
                                    <div
                                        className={styles.bar}
                                        style={{ height: `${Math.max(2, (a.count / maxActivity) * 100)}%` }}
                                    />
                                    {i % 7 === 0 && (
                                        <span className={styles.barLabel}>
                                            {new Date(a.date).toLocaleDateString('ru-RU', { month: 'short', day: 'numeric' })}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Language stats */}
                {topLangs.length > 0 && (
                    <div className={styles.section} style={{ flex: '0 0 280px' }}>
                        <h3 className={styles.sectionTitle}>Языки</h3>
                        <div className={styles.langBar}>
                            {topLangs.map(([lang, bytes]) => (
                                <div
                                    key={lang}
                                    className={styles.langSegment}
                                    style={{ width: `${(bytes / langTotal) * 100}%`, background: LANG_COLORS[lang] || '#8b5cf6' }}
                                    title={`${lang}: ${((bytes / langTotal) * 100).toFixed(1)}%`}
                                />
                            ))}
                        </div>
                        <div className={styles.langList}>
                            {topLangs.map(([lang, bytes]) => (
                                <div key={lang} className={styles.langItem}>
                                    <span className={styles.langDot} style={{ background: LANG_COLORS[lang] || '#8b5cf6' }} />
                                    <span className={styles.langName}>{lang}</span>
                                    <span className={styles.langPct}>{((bytes / langTotal) * 100).toFixed(1)}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Contributors */}
            {commits_by_author.length > 0 && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Активность участников</h3>
                    <div className={styles.contributorList}>
                        {commits_by_author.map((c, i) => {
                            const maxC = commits_by_author[0].commits;
                            return (
                                <div key={i} className={styles.contributorRow}>
                                    <div className={styles.contributorInfo}>
                                        {c.avatar
                                            ? <img src={c.avatar} alt={c.username} className={styles.cAvatar} />
                                            : <div className={styles.cAvatarFallback}>{(c.username || '?')[0].toUpperCase()}</div>
                                        }
                                        <span className={styles.cUsername}>{c.username}</span>
                                        <span className={styles.cCommits}>{c.commits} коммит(ов)</span>
                                    </div>
                                    <div className={styles.cBarWrap}>
                                        <div
                                            className={styles.cBar}
                                            style={{ width: `${(c.commits / maxC) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
